import { ConfigManager, ConfigLayer, defaultSchemas } from '../core/config-manager';
import { WorkflowConfig } from '../types';
import { z } from 'zod';

// 单例配置管理器
let configManager: ConfigManager | null = null;

// WorkflowConfig Schema - 与 types/index.ts 中的 WorkflowConfig 接口保持一致
const workflowConfigSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Version must be in format x.x.x'),
  project: z.object({
    name: z.string(),
    type: z.string(),
    framework: z.array(z.string()),
    language: z.string()
  }),
  settings: z.object({
    autoSync: z.boolean(),
    syncInterval: z.number().min(1000),
    enableAI: z.boolean(),
    checkMode: z.enum(['quick', 'normal', 'full'])
  }),
  paths: z.object({
    todoFile: z.string().optional(),
    workflowDir: z.string().optional()
  }).optional()
});

/**
 * 获取或创建配置管理器
 */
export async function getConfigManager(projectRoot?: string): Promise<ConfigManager> {
  if (!configManager) {
    configManager = new ConfigManager({
      projectRoot: projectRoot || process.cwd(),
      schemas: {
        ...defaultSchemas,
        workflow: workflowConfigSchema
      },
      encryption: {
        enabled: !!process.env.WORKFLOW_ENCRYPTION_ENABLED,
        secretKey: process.env.WORKFLOW_SECRET_KEY
      },
      watch: process.env.NODE_ENV !== 'test'
    });
    
    await configManager.load();
  }
  
  return configManager;
}

/**
 * 加载工作流配置
 */
export async function loadConfig(workflowDir: string): Promise<WorkflowConfig> {
  const manager = await getConfigManager(workflowDir);
  const config = manager.get('workflow');
  
  if (!config) {
    throw new Error('Workflow configuration not found. Run "workflow init" first.');
  }
  
  return config as WorkflowConfig;
}

/**
 * 保存工作流配置
 */
export async function saveConfig(workflowDir: string, config: WorkflowConfig): Promise<void> {
  const manager = await getConfigManager(workflowDir);
  await manager.set('workflow', config, ConfigLayer.PROJECT);
}

/**
 * 更新工作流配置
 */
export async function updateConfig(
  workflowDir: string,
  updates: Partial<WorkflowConfig>
): Promise<WorkflowConfig> {
  const manager = await getConfigManager(workflowDir);
  const currentConfig = manager.get('workflow', {}) as WorkflowConfig;
  const updatedConfig = { ...currentConfig, ...updates };
  
  await manager.set('workflow', updatedConfig, ConfigLayer.PROJECT);
  return updatedConfig;
}

/**
 * 监听配置变化
 */
export function watchConfig(
  workflowDir: string,
  callback: (config: WorkflowConfig) => void | Promise<void>
): () => void {
  let unwatch: (() => void) | null = null;
  
  (async () => {
    const manager = await getConfigManager(workflowDir);
    unwatch = manager.watch('workflow', async (event) => {
      await callback(event.newValue as WorkflowConfig);
    });
  })();
  
  return () => {
    if (unwatch) {
      unwatch();
    }
  };
}

/**
 * 验证配置
 */
export async function validateConfig(config: any): Promise<{ valid: boolean; errors?: string[] }> {
  try {
    workflowConfigSchema.parse(config);
    return { valid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        valid: false,
        errors: error.errors.map(e => `${e.path.join('.')}: ${e.message}`)
      };
    }
    throw error;
  }
}

/**
 * 重置配置
 */
export async function resetConfig(workflowDir: string): Promise<void> {
  const manager = await getConfigManager(workflowDir);
  await manager.reset(ConfigLayer.PROJECT);
}

/**
 * 导出配置
 */
export async function exportConfig(workflowDir: string): Promise<WorkflowConfig> {
  const manager = await getConfigManager(workflowDir);
  return manager.export(ConfigLayer.PROJECT).workflow as WorkflowConfig;
}

/**
 * 导入配置
 */
export async function importConfig(workflowDir: string, config: WorkflowConfig): Promise<void> {
  const manager = await getConfigManager(workflowDir);
  await manager.import({ workflow: config }, ConfigLayer.PROJECT);
}

/**
 * 清理资源
 */
export async function dispose(): Promise<void> {
  if (configManager) {
    await configManager.dispose();
    configManager = null;
  }
}